<template lang="jade">
  //- Click the load button to load EDID
  button.btn.btn-primary(v-on:click="download" v-if='!isMobile') Download
  a(v-el:download-tag v-show="false")
  div(v-el:content)
    h2 EDID HEADER
    p(v-for="(h,k) in headers")
      | {{ k }} : {{ result[h] }}
    h2 Basic Display Parameters
    p Digital Input: {{ result.bdp.digitalInput }}
    p Compatible with VESA DFP: {{ result.bdp.vesaDfpCompatible }}
    p Maximum Horizontal Image Size: {{ result.bdp.maxHorImgSize }}
    p Maximum Vertical Image Size: {{ result.bdp.maxVertImgSize }}
    p Display Gamma: {{ result.bdp.displayGamma.toFixed(2) }}
    p DPMS Standby: {{ result.bdp.dpmsStandby }}
    p DPMS Suspend: {{ result.bdp.dpmsSuspend }}
    p DPMS Active-Off: {{ result.bdp.dpmsActiveOff }}
    p Display Type: {{ result.digitalColorSpace[result.bdp.displayType] }}
    p Standard sRGB Color Space: {{ result.bdp.standardSRgb }}
    p Preferred Timing Mode: {{ result.bdp.preferredTiming }}
    p GTF Supported: {{ result.bdp.gtfSupported }}
    template(v-if="result.bdp.digitalInput")
      h2 Anolog BDP
      p Digital Input: {{ result.bdp.digitalInput }}
      p Video white and sync levels, relative to blank: {{ result.WhiteAndSyncLevels[result.bdp.whiteSyncLevels] }}
      p Blank-to-black setup (pedestal) expected: {{ result.bdp.blankToBlack }}
      p Separate sync supported: {{ result.bdp.separateSyncSupported }}
      p Composite sync (on HSync) supported: {{ result.bdp.compositeSyncSupported }}
      p Sync on green supported: {{ result.bdp.synOnGreen }}
      p VSync pulse must be serrated: {{ result.bdp.vsyncSerrated }}
      p Maximum Horizontal Image Size: {{ result.bdp.maxHorImgSize }}
      p Maximum Vertical Image Size: {{ result.bdp.maxVertImgSize }}
      p Display Gamma: {{ result.bdp.displayGamma.toFixed(2) }}
      p DPMS Standby: {{ result.bdp.dpmsStandby }}
      p DPMS Suspend: {{ result.bdp.dpmsSuspend }}
      p DPMS Active-Off: {{ result.bdp.dpmsActiveOff }}
      p Display Type: {{ result.digitalColorSpace[result.bdp.displayType] }}
      p Standard sRGB Color Space: {{ result.bdp.standardSRgb }}
      p Preferred Timing Mode: {{ result.bdp.preferredTiming }}
      p GTF Supported: {{ result.bdp.gtfSupported }}
    h2 Chromaticity Coordinates
    p Red X: {{ result.chromaticity.redXCoords.toFixed(3) }}
    p Red Y: {{ result.chromaticity.redYCoords.toFixed(3) }}
    p Green X: {{ result.chromaticity.greenXCoords.toFixed(3) }}
    p Green Y: {{ result.chromaticity.greenYCoords.toFixed(3) }}
    p Blue X: {{ result.chromaticity.blueXCoords.toFixed(3) }}
    p Blue Y: {{ result.chromaticity.blueYCoords.toFixed(3) }}
    p White X: {{ result.chromaticity.whiteXCoords.toFixed(3) }}
    p White Y: {{ result.chromaticity.whiteYCoords.toFixed(3) }}
    h2 Timing Bitmap
    p(v-for="tb in result.establishedTimingBitmaps")
      | {{tb}} : {{ isTimingBitmapSet($index) }}
    h2 Standard Display Modes
    ul(v-for="dispMode in result.standardDisplayModes")
      li Resolution: {{ dispMode.xResolution }}
      li X:Y Pixel Ratio: {{result.xyPixelRatioEnum[dispMode.xyPixelRatio].string}}
      li Vertical Frequency: {{dispMode.vertFreq}}
    h2 Standard Display Modes
    div(v-for="dtd in result.dtds")
      ul Detailed Timing Descriptor {{ $index + 1 }}
        li Pixel Clock: {{dtd.pixelClock}}MHz
        li Horizontal Active: {{dtd.horActivePixels}}
        li Horizontal Blanking: {{dtd.horBlankPixels}}
        li Vertical Active: {{dtd.vertActivePixels}}
        li Vertical Blanking: {{dtd.vertBlankPixels}}
        li Horizontal Sync Offset: {{dtd.horSyncOff}}
        li Horizontal Sync Pulse: {{dtd.horSyncPulse}}
        li Vertical Sync Offset: {{dtd.vertSyncOff}}
        li Vertical Sync Pulse: {{dtd.vertSyncPulse}}
        li Horizontal Display Size: {{dtd.horDisplaySize}}
        li Vertical Display Size: {{dtd.vertDisplaySize}}
        li Horizontal Border: {{dtd.horBorderPixels}}
        li Vertical Border: {{dtd.vertBorderLines}}
        li Interlaced: {{dtd.interlaced}}
        li Stereo Mode: {{dtd.stereoMode}}
        li Sync Type: {{dtd.syncType}}
        li Vertical Sync {{ dtd.syncType == result.syncTypeEnum.DIGITAL_SEPARATE ? 'Polarity' : 'Serrated' }}: {{dtd.vSyncPolarity}}
        li(v-show="dtd.syncType == result.syncTypeEnum.DIGITAL_SEPARATE") Vertical Sync Polarity: {{dtd.vSyncPolarity}}
        li(v-show="dtd.syncType == result.syncTypeEnum.DIGITAL_SEPARATE") Vertical Sync Polarity: {{dtd.vSyncPolarity}}
        template(v-if="(dtd.syncType == result.syncTypeEnum.ANALOG_COMPOSITE) || (dtd.syncType == result.syncTypeEnum.BIPOLAR_ANALOG_COMPOSITE)")
          li Sync on all 3 RGB lines: {{dtd.syncAllRGBLines}}
        template(v-else)
          li HSync polarity: {{dtd.hSyncPolarity}}
          li 2-Way Line-Interleaved Stereo: {{dtd.twoWayStereo}}
    div(v-for="ext in result.exts")
      h2 BLOCK {{ ext.blockNumber }}
      h3 Extension Header Information
      p Valid Checksum: {{validChecksums[ext.blockNumber]}}
      p Extension Tag: {{ext.extTag}}
      p Revision Number: {{ext.revisionNumber}}
      p DTD Start: 0x{{ext.dtdStart.toString(16).toUpperCase()}}
      p Number of Native DTDs: {{ext.numDtds}}
      p Supports Underscan: {{ ext.underscan }}
      p Supports Basic Audio: {{ ext.basicAudio }}
      p Supports YcBcR 4:4:4: {{ ext.ycbcr444 }}
      p Supports YcBcR 4:2:2: {{ ext.ycbcr422 }}
      p Checksum: 0x{{ext.checksum.toString(16).toUpperCase()}}
      .new
        div(v-for="dbc in ext.dataBlockCollection" track-by='$index')
          p
            div(v-if="dbc.tag.value == 0")
              h3 Reserved Data Block
              p Insert Audio Descriptor! <br />
            div(v-if="dbc.tag.value == 2")
              //- VIDEO
              h3 VIDEO
              ul(v-for="svd in dbc.shortVideoDescriptors")
                li Native: {{ svd.nativeResolution.toString().toUpperCase() }}
                li VIC: {{ result.shortVideoDescriptors[svd.vic].vic }}
                li Format: {{ result.shortVideoDescriptors[svd.vic].format }}
                li Field Rate: {{ result.shortVideoDescriptors[svd.vic].fieldRate }}
                li Picture AR: {{ result.shortVideoDescriptors[svd.vic].pictureAspectRatio }}
                li Pixel AR: {{ result.shortVideoDescriptors[svd.vic].pixelAspectRatio }}
            div(v-if="dbc.tag.value == 1")
              //- AUDIO
              h3 AUDIO
              ul(v-for="sad in dbc.shortAudioDescriptors")
                p Format Code: {{ result.shortAudioDescriptors[sad.format] }}
                p Max Channels: {{ sad.maxChannels }}
                div
                  p Sample Rates:
                  p(v-for="sampleRate in result.sadSampleRates") {{ sampleRate }} {{ isAudioBitSet(sad.sampleRates ,$index) }}
                div(v-if="audioType(sad.format) == 'lpcm' ")
                  p Bit Depth:
                  p(v-for="bitDepth in result.sadBitDepths") {{ bitDepth }} {{ isAudioBitSet(sad.bitDepth,$index) }}
                div(v-if="audioType(sad.format) == 'twoEight' ")
                  p Maximum Bit Rate: {{ sad.bitRate }}
                div(v-if="audioType(sad.format) == 'nineThirteen' ")
                  p Audio Format Code: {{ sad.audioFormatCode }}
                div(v-if="audioType(sad.format) == 'wmaPro' ")
                  p Format Code Ext.: {{ sad.formatCodeExt }}
                div(v-if="audioType(sad.format) == 'extension' ")
                  p Profile: {{ sad.profile }}
            div(v-if="dbc.tag.value == 4")
              //- SPEAKER ALLOCATION
              h3 Speaker Allocation Data Block
              p(v-for="speakerAllocation in result.speakerAllocation") {{ speakerAllocation }} {{ isAudioBitSet(dbc.payload,$index) }}
            div(v-if="dbc.tag.value == 3")
              //- VENDOR_SPECIFIC
              h3 Vendor Specific Data Block
              p IEEE OUI: {{ pad(dbc.ieeeIdentifier.toString(16).toUpperCase(),6) }}
              div(v-if="dbc.ieeeIdentifier == result.ieeeOuiType.HDMI14.value")
                p Source Physical Address: {{ dbc.physicalAddress.toString(16).toUpperCase() }}
                p Supports ACP, ISRC1, ISRC2 {{ dbc.supportsAI }}
                p Supports 48 bits/pixel (16 bits/color) {{ dbc.deepColor48 }}
                p Supports 36 bits/pixel (12 bits/color) {{ dbc.deepColor36 }}
                p Supports 30 bits/pixel (10 bits/color) {{ dbc.deepColor30 }}
                p Supports YCbCr 4:4:4 Deep Color {{ dbc.deepColorY444 }}
                p Supports Dual DVI {{ dbc.dualDvi }}
                p Max TMDS Clock Rate: {{dbc.maxTmdsRate}} MHz
                p Video/Audio Latency Fields Present {{ dbc.latencyPresent }}
                div(v-show="dbc.latencyPresent")
                  p Video Latency: {{dbc.videoLatency}} ms
                  p Audio Latency: {{dbc.audioLatency}} ms
                  p Interlaced Video/Audio Latency Fields Present: {{dbc.latencyPresent.toString().toUpperCase()}}
                  p Interlaced Video Latency: {{dbc.iVideoLatency}} ms
                  p Interlaced Audio Latency: {{dbc.iAudioLatency}} ms
              div(v-if="dbc.ieeeIdentifier == result.ieeeOuiType.HDMI20.value")
                p Version: {{ dbc.versionHF }}
                p Max TMDS Clock Rate: {{ dbc.maxTmdsRateHF }} MHz
                p Supports SCDC {{ dbc.supportsSCDC }}
                p Supports SCDC Read Request {{ dbc.supportsSCDCRR }}
                p Supports Scrambling for less &lt;=340MHz {{ dbc.supportsLTE340scramble }}
                p Supports 3D Independent View {{ dbc.supports3DIV }}
                p Supports 3D Dual View {{ dbc.supports3DDV }}
                p Supports 3D OSD Disparity {{ dbc.supports3DOSD }}
                p Supports 30 bits/pixel (10 bits/color) for Deep Color YCbCr 4:2:0 {{ dbc.deepColorY420_30 }}
                p Supports 36 bits/pixel (12 bits/color) for Deep Color YCbCr 4:2:0 {{ dbc.deepColorY420_36 }}
                p Supports 48 bits/pixel (16 bits/color) for Deep Color YCbCr 4:2:0 {{ dbc.deepColorY420_48 }}
            div(v-if="dbc.tag.value == 7")
              div(v-if="dbc.extendedTag.value == 0")
                //- videoCapability
                h3 Video Capability Data Block
                p Quantization Range selectable via AVI YQ for YCC formats {{ dbc.quantizationRangeYCC }}
                p Quantization Range selectable via AVI Q for RGB formats {{ dbc.quantizationRangeRGB }}
                p Overscan underscan behaviour for Preferred Video Format: {{ceaDataBlock.overscanPT}}
                p Overscan underscan behaviour for IT Video Formats: {{ceaDataBlock.overscanIT}}
                p Overscan underscan behaviour for CE Video Formats: {{ceaDataBlock.overscanCE}}
              div(v-if="dbc.extendedTag.value == 5")
                //- colorimetry
                h3 Colorimetry Data Block
                p Supports BT2020 RGB {{ dbc.supportsBT2020RGB }}
                p Supports BT2020 YCC {{ dbc.supportsBT2020YCC }}
                p Supports BT2020 cYCC {{ dbc.supportsBT2020cYCC }}
                p Supports Adobe RGB {{ dbc.supportsAdobeRGB }}
                p Supports Adobe YCC601 {{ dbc.supportsAdobeYCC601 }}
                p Supports sYCC601 {{ dbc.supportssYCC601 }}
                p Supports xvYCC709 {{ dbc.supportsxvYCC709 }}
                p Supports xvYCC601 {{ dbc.supportsxvYCC601 }}
                p Gamut MetaData3 (MD3): {{ dbc.gamutMD3}}
                p Gamut MetaData2 (MD2): {{ dbc.gamutMD2}}
                p Gamut MetaData1 (MD1): {{ dbc.gamutMD1}}
                p Gamut MetaData0 (MD0): {{ dbc.gamutMD0}}
              div(v-if="dbc.extendedTag.value == 14")
                //- YCbCr420Video
                h3 YCbCr 4:2:0 Video Data Block
                p The following modes support only YCbCr 4:2:0 color format.
                div(v-for="svd in dbc.YCbCr420OnlyShortVideoDescriptors")
                  p Native: {{svd.nativeResolution.toString().toUpperCase() }}
                  p VIC: {{ result.shortVideoDescriptors[svd.vic].vic }}
                  p Format: {{ result.shortVideoDescriptors[svd.vic].format }}
                  p Field Rate: {{ result.shortVideoDescriptors[svd.vic].fieldRate }}
                  p Picture AR: {{ result.shortVideoDescriptors[svd.vic].pictureAspectRatio }}
                  p Pixel AR: {{ result.shortVideoDescriptors[svd.vic].pixelAspectRatio }}
              div(v-if="dbc.extendedTag.value == 15")
                //- YCbCr420CapabilityMap

                h3 YCbCr 4:2:0 Capability Map Data Block</h3>
                p The following modes support YCbCr 4:2:0 in addition to other color formats.
                div(v-for="svd in dbc.YCbCr420CapableShortVideoDescriptors")
                  p Native: {{svd.nativeResolution.toString().toUpperCase()}}
                  p VIC: {{ result.shortVideoDescriptors[svd.vic].vic}}
                  p Format: {{ result.shortVideoDescriptors[svd.vic].format}}
                  p Field Rate: {{ result.shortVideoDescriptors[svd.vic].fieldRate}}
                  p Picture AR: {{ result.shortVideoDescriptors[svd.vic].pictureAspectRatio}}
                  p Pixel AR: {{ result.shortVideoDescriptors[svd.vic].pixelAspectRatio}}
</template>

<script>
  import parseEDID from '../../utils/edidParser'
  import MobileDetect from 'mobile-detect'
  export default {
    props: {
      edidString: String
    },
    data: () => ({
      headers: {
        validHeader: 'Valid',
        productCode: 'Product Code',
        serialNumber: 'Serial Number',
        manufactureDate: 'Manufacture Date',
        edidVersion: 'Edid Version',
        numberOfExtensions: 'No Of Extensions',
        checksum: 'Checksum',
        isMobile: false
      }
    }),
    computed: {
      isMobile () {
        let md = new MobileDetect(window.navigator.userAgent)
        return md.mobile() !== null
      },
      validChecksums () {
        let numberBlocks = this.result.edidData.length / this.result.EDID_BLOCK_LENGTH
        let cks = []
        for (let i = 0; i < numberBlocks; i++) {
          cks.push(this.result.validChecksum(i).toString().toUpperCase())
        }
        return cks
      },
      result () {
        return parseEDID(this.edidString)
      }
    },
    methods: {
      isTimingBitmapSet (index) {
        let msb = 0x800000
        return (this.result.timingBitmap & (msb >> index)) !== 0
      },
      isAudioBitSet (testByte, index) {
        return (testByte & (1 << index)) !== 0
      },
      pad (num, size) {
        let s = num + ''
        while (s.length < size) s = '0' + s
        return s
      },
      audioType (format) {
        if (format <= this.result.audioFormatArray[0]) return 'lpcm'
        else if (format <= this.result.audioFormatArray[1]) return 'twoEight'
        else if (format <= this.result.audioFormatArray[2]) return 'nineThirteen'
        else if (format <= this.result.audioFormatArray[3]) return 'wmaPro'
        else return 'extension'
      },
      download () {
        let textFileAsBlob = new window.Blob([this.$els.content.innerText], {type: 'text/plain'})
        let atag = this.$els.downloadTag
        atag.download = 'edid.txt'
        atag.href = window.URL.createObjectURL(textFileAsBlob)
        try {
          atag.click()
        } catch (e) {  // throws when IE
          window.navigator.msSaveBlob(textFileAsBlob, atag.download)
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
@import '~__vendor_variables_scss__';
@import '~sass/_breakpoint.scss';
  p {
    padding-left: 12px;
  }
  ul {
    li {
      padding-left: 12px;
    }
  }

  @include breakpoint(xxs) {
    p {
      font-size: 12px;
    }
    h2{
      font-size: 16px;
      font-weight: 900;
    }
  }
</style>>



// WEBPACK FOOTER //
// EdidDisplayer.vue?2ff0c44c